import React, { useState } from 'react'
import { motion } from 'framer-motion'
import {
  PlusIcon,
  HeartIcon,
  ChatBubbleLeftIcon,
  ShareIcon,
  BookmarkIcon,
  EllipsisHorizontalIcon,
  PhotoIcon
} from '@heroicons/react/24/outline'
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid'

const StoriesPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'timeline' | 'trending' | 'following'>('timeline')

  const tabs = [
    { id: 'timeline', label: 'Timeline', count: null },
    { id: 'trending', label: 'Trending', count: '12' },
    { id: 'following', label: 'Following', count: '5' },
  ]

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-4xl font-display font-bold gradient-text">Story Timeline</h1>
          <p className="text-white/70 mt-2">Share your journey, connect through stories</p>
        </div>
        
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="btn-primary"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Create Story
        </motion.button>
      </div>

      {/* Tabs */}
      <div className="card-glass p-1">
        <div className="flex space-x-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-primary-600 to-secondary-600 text-white'
                  : 'text-white/70 hover:text-white hover:bg-white/5'
              }`}
            >
              {tab.label}
              {tab.count && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-accent-500 text-white">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Story Feed */}
      <div className="space-y-8">
        <StoryCard
          author={{
            name: "Sarah Mitchell",
            avatar: "/api/placeholder/40/40",
            verified: true
          }}
          story={{
            title: "Chapter 3: Our First Adventure Together",
            content: "Last weekend marked a beautiful milestone in our journey. We decided to take that road trip we'd been planning for months. The mountains were calling, and we couldn't resist the urge to explore together...",
            images: ["/api/placeholder/600/400", "/api/placeholder/600/400"],
            timestamp: "2 hours ago",
            likes: 247,
            comments: 18,
            shares: 12,
            chapter: 3,
            totalChapters: 12
          }}
        />

        <StoryCard
          author={{
            name: "Alex Rivera",
            avatar: "/api/placeholder/40/40",
            verified: false
          }}
          story={{
            title: "The Digital Connection",
            content: "Meeting someone online used to feel impersonal to me. But our late-night conversations, video calls, and shared playlists created something deeper than I ever expected. Technology became the bridge that connected our hearts across the distance.",
            images: ["/api/placeholder/600/400"],
            timestamp: "5 hours ago",
            likes: 189,
            comments: 32,
            shares: 8,
            chapter: 1,
            totalChapters: 1
          }}
        />

        <StoryCard
          author={{
            name: "Maya Patel",
            avatar: "/api/placeholder/40/40",
            verified: true
          }}
          story={{
            title: "Chapter 7: Moving In Together",
            content: "Today we got the keys to our first place together! It's a small apartment, but it's ours. I never thought combining two lives would involve so many boxes, but every unpacked item feels like we're building our future, one piece at a time.",
            images: ["/api/placeholder/600/400", "/api/placeholder/600/400", "/api/placeholder/600/400"],
            timestamp: "1 day ago",
            likes: 512,
            comments: 67,
            shares: 23,
            chapter: 7,
            totalChapters: 15
          }}
        />
      </div>

      {/* Load More */}
      <div className="flex justify-center">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="btn-glass px-8 py-3"
        >
          Load More Stories
        </motion.button>
      </div>
    </div>
  )
}

interface Author {
  name: string
  avatar: string
  verified: boolean
}

interface Story {
  title: string
  content: string
  images: string[]
  timestamp: string
  likes: number
  comments: number
  shares: number
  chapter: number
  totalChapters: number
}

interface StoryCardProps {
  author: Author
  story: Story
}

const StoryCard: React.FC<StoryCardProps> = ({ author, story }) => {
  const [isLiked, setIsLiked] = useState(false)
  const [isSaved, setIsSaved] = useState(false)

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="story-card"
    >
      {/* Author Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <img
            src={author.avatar}
            alt={author.name}
            className="w-12 h-12 rounded-full bg-gradient-to-r from-primary-600 to-secondary-600 p-0.5"
          />
          <div>
            <div className="flex items-center">
              <h3 className="font-semibold text-white">{author.name}</h3>
              {author.verified && (
                <svg className="w-4 h-4 ml-1 text-primary-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <p className="text-sm text-white/50">{story.timestamp}</p>
          </div>
        </div>
        
        <button className="p-2 rounded-full hover:bg-white/10 transition-colors">
          <EllipsisHorizontalIcon className="h-5 w-5 text-white/70" />
        </button>
      </div>

      {/* Chapter Progress */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-sm text-white/70 mb-2">
          <span>Chapter {story.chapter} of {story.totalChapters}</span>
          <span>{Math.round((story.chapter / story.totalChapters) * 100)}% complete</span>
        </div>
        <div className="w-full bg-white/10 rounded-full h-2">
          <div 
            className="bg-gradient-to-r from-primary-600 to-secondary-600 h-2 rounded-full"
            style={{ width: `${(story.chapter / story.totalChapters) * 100}%` }}
          />
        </div>
      </div>

      {/* Story Title */}
      <h2 className="text-xl font-bold mb-4">{story.title}</h2>

      {/* Story Content */}
      <p className="text-white/80 leading-relaxed mb-6">{story.content}</p>

      {/* Story Images */}
      {story.images.length > 0 && (
        <div className={`mb-6 grid gap-2 rounded-xl overflow-hidden ${
          story.images.length === 1 ? 'grid-cols-1' :
          story.images.length === 2 ? 'grid-cols-2' :
          'grid-cols-2 md:grid-cols-3'
        }`}>
          {story.images.map((_, index) => (
            <div
              key={index}
              className="aspect-w-16 aspect-h-9 bg-gradient-to-br from-primary-500/20 to-secondary-500/20 rounded-lg flex items-center justify-center"
            >
              <PhotoIcon className="h-12 w-12 text-white/30" />
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-white/10">
        <div className="flex items-center space-x-6">
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => setIsLiked(!isLiked)}
            className="flex items-center space-x-2 text-white/70 hover:text-white transition-colors"
          >
            {isLiked ? (
              <HeartSolidIcon className="h-6 w-6 text-red-500 heart-pulse" />
            ) : (
              <HeartIcon className="h-6 w-6" />
            )}
            <span>{story.likes + (isLiked ? 1 : 0)}</span>
          </motion.button>

          <button className="flex items-center space-x-2 text-white/70 hover:text-white transition-colors">
            <ChatBubbleLeftIcon className="h-6 w-6" />
            <span>{story.comments}</span>
          </button>

          <button className="flex items-center space-x-2 text-white/70 hover:text-white transition-colors">
            <ShareIcon className="h-6 w-6" />
            <span>{story.shares}</span>
          </button>
        </div>

        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={() => setIsSaved(!isSaved)}
          className={`p-2 rounded-full transition-colors ${
            isSaved ? 'text-accent-400' : 'text-white/70 hover:text-white'
          }`}
        >
          <BookmarkIcon className={`h-6 w-6 ${isSaved ? 'fill-current' : ''}`} />
        </motion.button>
      </div>
    </motion.article>
  )
}

export default StoriesPage